import i18next from 'i18next';

const TOAST_CONTAINER_ID = 'feature-toast-container';
const MAX_VISIBLE_EPHEMERAL_TOASTS = 3;
const VALID_VARIANTS = new Set(['success', 'info', 'warning', 'danger']);
const DEFAULT_DURATIONS = {
    success: 5000,
    info: 5000,
    warning: 8000,
    danger: null,
};
const ALERT_CLASS_VARIANTS = {
    'alert-success': 'success',
    'alert-info': 'info',
    'alert-warning': 'warning',
    'alert-danger': 'danger',
};
const LEGACY_ALERT_DURATIONS = {
    success: 5000,
    info: 5000,
    warning: 8000,
    danger: 8000,
};

let nextToastId = 0;
const toastTimers = new Map();

const normalizeVariant = (variant) => (
    VALID_VARIANTS.has(variant) ? variant : 'success'
);

const ensureToastContainer = () => {
    let container = document.getElementById(TOAST_CONTAINER_ID);
    if (container) {
        return container;
    }
    container = document.createElement('div');
    container.id = TOAST_CONTAINER_ID;
    container.className = 'feature-toast-container';
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-relevant', 'additions removals');
    document.body.appendChild(container);
    return container;
};

const clearToastTimer = (toastId) => {
    const timer = toastTimers.get(toastId);
    if (timer) {
        window.clearTimeout(timer);
        toastTimers.delete(toastId);
    }
};

const dismissToastElement = (toast) => {
    if (!toast) {
        return;
    }
    clearToastTimer(toast.dataset.toastId);
    toast.remove();
};

const appendToastAction = (body, action) => {
    const label = String(action?.label || '').trim();
    const href = String(action?.href || '').trim();
    if (!label || !href || /^javascript:/i.test(href)) {
        return;
    }
    const link = document.createElement('a');
    link.className = 'feature-toast__action';
    link.textContent = label;
    link.href = href;
    body.appendChild(link);
};

const enforceEphemeralToastLimit = (container) => {
    const ephemeralToasts = () => Array.from(container.children)
        .filter((toast) => toast.dataset.persistent !== 'true');
    while (ephemeralToasts().length > MAX_VISIBLE_EPHEMERAL_TOASTS) {
        dismissToastElement(ephemeralToasts()[0]);
    }
};

export const showToast = ({
    message,
    variant = 'success',
    action = null,
    duration,
} = {}) => {
    const normalizedVariant = normalizeVariant(variant);
    const resolvedDuration = duration === undefined
        ? DEFAULT_DURATIONS[normalizedVariant]
        : duration;
    const isPersistent = !(Number.isFinite(resolvedDuration) && resolvedDuration > 0);
    const toastId = `feature-toast-${++nextToastId}`;
    const container = ensureToastContainer();
    const toast = document.createElement('div');
    toast.className = `feature-toast feature-toast--${normalizedVariant}`;
    toast.dataset.toastId = toastId;
    toast.dataset.persistent = String(isPersistent);
    toast.setAttribute('role', normalizedVariant === 'danger' ? 'alert' : 'status');

    const body = document.createElement('div');
    body.className = 'feature-toast__body';
    const text = document.createElement('span');
    text.className = 'feature-toast__message';
    text.textContent = String(message || '');
    body.appendChild(text);
    appendToastAction(body, action);

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'feature-toast__close btn-close';
    closeButton.setAttribute('aria-label', i18next.t('common:close', {defaultValue: 'Close'}));
    closeButton.addEventListener('click', () => dismissToastElement(toast));

    toast.append(body, closeButton);
    container.appendChild(toast);
    if (!isPersistent) {
        const timer = window.setTimeout(() => dismissToastElement(toast), resolvedDuration);
        toastTimers.set(toastId, timer);
    }
    enforceEphemeralToastLimit(container);
    window.requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
    });

    return {
        id: toastId,
        dismiss: () => dismissToastElement(toast),
    };
};

const variantFromAlertClass = (className) => {
    const classNames = String(className || '').split(/\s+/);
    const matchingClassName = classNames.find((name) => ALERT_CLASS_VARIANTS[name]);
    return ALERT_CLASS_VARIANTS[matchingClassName] || 'info';
};

export const displayAlert = (message, className) => {
    const variant = variantFromAlertClass(className);
    return showToast({
        message,
        variant,
        duration: LEGACY_ALERT_DURATIONS[variant],
    });
};

export const displayError = (message = 'Error') => (
    displayAlert(message, 'alert-danger')
);

export const displaySuccess = (message = 'Success') => (
    displayAlert(message, 'alert-success')
);
